"use client";

import "./globals.css";
import { motion } from "framer-motion";
import { AlertCircle, Check, CircleHelp, Copy, RotateCcw } from "lucide-react";
import { ReactNode, useEffect, useState } from "react";
import { useChat } from "react-live-chat-loader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { trackEvent } from "@/shared/analytics-wrapper";

type ErrorStep = {
  title: string;
  description: string;
  action?: {
    label: string;
    onClick: () => void;
    icon?: ReactNode;
  };
};

export default function Error({
  error,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const [browser, setBrowser] = useState<string>("");
  const [copied, setCopied] = useState(false);

  const [_, loadChat] = useChat();

  useEffect(() => {
    trackEvent({
      eventName: "errorPageShown",
      action: "view",
      category: `${error.name}: ${error.message}`,
    });

    // Detect browser
    if (typeof window !== "undefined") {
      const userAgent = navigator.userAgent;
      if (userAgent.indexOf("Chrome") > -1) {
        setBrowser("Chrome");
      } else if (userAgent.indexOf("Safari") > -1) {
        setBrowser("Safari");
      } else if (userAgent.indexOf("Firefox") > -1) {
        setBrowser("Firefox");
      } else if (userAgent.indexOf("MSIE") > -1 || userAgent.indexOf("Trident") > -1) {
        setBrowser("Internet Explorer");
      } else if (userAgent.indexOf("Edge") > -1) {
        setBrowser("Edge");
      } else {
        setBrowser("your browser");
      }
    }
  }, []);

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy URL:", err);
    }
  };

  const errorSteps: ErrorStep[] = [
    {
      title: "Refresh the page",
      description: "This often resolves temporary issues with the application.",
      action: {
        label: "Refresh now",
        onClick: () => window.location.reload(),
        icon: <RotateCcw className="mr-2 h-4 w-4" />,
      },
    },
    {
      title: `Restart ${browser}`,
      description: `Quit ${browser} completely and reopen it to clear any browser-related issues.`,
    },
    {
      title: "Try a different browser",
      description:
        "If the problem persists, try opening this page in a different browser like Chrome, Firefox, or Safari.",
      action: {
        label: copied ? "URL Copied!" : "Copy URL",
        onClick: copyUrl,
        icon: copied ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />,
      },
    },
    {
      title: "Reach out to our team",
      description:
        "If none of the above steps resolve the issue, our support team is here to help.",
      action: {
        label: "Contact Support",
        onClick: () => {
          loadChat({ open: true });
        },
        icon: <CircleHelp className="mr-2 h-4 w-4" />,
      },
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="flex min-h-screen flex-col items-center justify-center p-4 md:p-8"
    >
      <Card className="w-full max-w-2xl overflow-hidden shadow-lg">
        <div className="p-6 md:p-8">
          <div className="mb-6 flex flex-row items-start gap-4 md:flex-row md:items-center">
            <div className="bg-destructive/10 rounded-full p-3">
              <AlertCircle className="text-destructive size-6 md:size-8" />
            </div>
            <div>
              <h1 className="text-lg font-bold tracking-tight md:text-2xl">Something went wrong</h1>
              <p className="text-muted-foreground mt-1 text-sm md:text-base">
                We apologize for the inconvenience. Let&apos;s try to resolve this issue.
              </p>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <h2 className="mb-3 text-lg font-medium">Try these steps:</h2>
              <div className="space-y-4">
                {errorSteps.map((step, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    className="flex gap-3"
                  >
                    <div className="flex-none">
                      <div className="bg-primary text-primary-foreground flex h-8 w-8 items-center justify-center rounded-full font-medium">
                        {index + 1}
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium">{step.title}</h3>
                      <p className="text-muted-foreground mt-1 text-sm">{step.description}</p>
                      {step.action && (
                        <Button
                          variant="secondary"
                          size="sm"
                          className="mt-2"
                          onClick={step.action.onClick}
                        >
                          {step.action.icon}
                          {step.action.label}
                        </Button>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Card>
    </motion.div>
  );
}
